import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = { 
    id: String, 
    applicantId: String, 
    weight: Number, 
    categoryType: String 
  }

  connect() {
    // Füge Event-Listener für Tastatur hinzu
    document.addEventListener('keydown', this.handleKeydown.bind(this))
    // Füge Event-Listener für Klicks außerhalb hinzu
    document.addEventListener('click', this.handleOutsideClick.bind(this))
  }

  disconnect() {
    // Entferne Event-Listener
    document.removeEventListener('keydown', this.handleKeydown.bind(this))
    document.removeEventListener('click', this.handleOutsideClick.bind(this))
  }

  select() {
    // Entferne Auswahl von anderen Kompetenzen
    document.querySelectorAll('.competency-selected').forEach(el => {
      el.classList.remove('competency-selected', 'ring-2', 'ring-blue-500', 'ring-offset-2', 'scale-105')
    })
    
    // Füge Auswahl zu dieser Kompetenz hinzu
    this.element.classList.add('competency-selected', 'ring-2', 'ring-blue-500', 'ring-offset-2', 'scale-105')
    
    // Zeige Hinweis für Tastatur-Bedienung
    this.showKeyboardHint()
  }

  handleKeydown(event) {
    // Nicht abfangen wenn der Benutzer in einem Eingabefeld tippt (z.B. neue Kompetenz wie "ProjeKtsteuerung")
    const active = document.activeElement
    const isInput = active && (
      active.tagName === 'INPUT' ||
      active.tagName === 'TEXTAREA' ||
      active.isContentEditable ||
      active.closest('[contenteditable="true"]')
    )
    if (isInput) return

    // Nur wenn eine Kompetenz ausgewählt ist
    if (!this.element.classList.contains('competency-selected')) return

    // Prüfe ob es eine Zahl zwischen 1-10 ist
    const key = event.key
    if (key >= '1' && key <= '9') {
      event.preventDefault()
      this.updateWeight(parseInt(key))
    } else if (key === '0') {
      event.preventDefault()
      this.updateWeight(10)
    } else if (key === 'k' || key === 'K') {
      event.preventDefault()
      this.clearWeight()
    } else if (key === 'Escape') {
      event.preventDefault()
      this.deselect()
    } else if (key === 'Delete') {
      event.preventDefault()
      this.deleteCompetency()
    }
  }

  updateWeight(newWeight) {
    const url = `/de/backend/applicants/${this.applicantIdValue}/applicant_competencies/${this.idValue}/update_weight`
    
    const formData = new FormData()
    formData.append('weight', newWeight)
    formData.append('_method', 'PATCH')

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Accept': 'text/vnd.turbo-stream.html',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    })
    .then(response => {
      if (response.ok) {
        // Aktualisiere das Weight-Badge
        this.updateWeightDisplay(newWeight)
        this.showSuccessMessage(newWeight)
        // Zeige Legende nach Update wieder an
        setTimeout(() => {
          this.showKeyboardHint()
        }, 100)
      } else {
        this.showErrorMessage('Fehler beim Aktualisieren der Gewichtung')
      }
    })
    .catch(error => {
      this.showErrorMessage('Fehler beim Aktualisieren der Gewichtung')
    })
  }

  updateWeightDisplay(newWeight) {
    const weightBadge = this.element.querySelector('[data-weight-select-target="display"]')
    if (weightBadge) {
      if (newWeight === null || newWeight === undefined) {
        // Keine Gewichtung
        weightBadge.textContent = this.categoryTypeValue === 'language' ? 'Keine Stufe' : 'Keine Gewichtung'
      } else if (this.categoryTypeValue === 'language') {
        // Für Sprachen: Zeige A1-C2 + Muttersprache Level
        const languageLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'Muttersprache']
        const levelText = languageLevels[newWeight - 1] || 'Keine Stufe'
        weightBadge.textContent = levelText
      } else {
        // Für andere Kompetenzen: Zeige Zahl
        weightBadge.textContent = newWeight
      }
    }
  }

  clearWeight() {
    this.updateWeight(null)
  }

  showKeyboardHint() {
    // Entferne vorherige Hinweise
    document.querySelectorAll('.keyboard-hint').forEach(el => el.remove())
    
    // Erstelle Hinweis
    const hint = document.createElement('div')
    hint.className = 'keyboard-hint fixed top-4 right-4 bg-blue-500/20 backdrop-blur-sm text-blue-900 px-4 py-2 rounded-lg shadow-lg z-50 text-sm border border-blue-200'
    hint.innerHTML = `
      <div class="font-bold mb-1">Tastatur-Bedienung:</div>
      <div>1-9: Gewichtung setzen</div>
      <div>0: Gewichtung 10</div>
      <div>K: Keine Gewichtung</div>
      <div>Entf: Kompetenz löschen</div>
      <div>ESC: Auswahl aufheben</div>
    `
    document.body.appendChild(hint)
    
    // Entferne Hinweis nach 5 Sekunden (länger anzeigen)
    setTimeout(() => {
      if (hint.parentNode) {
        hint.parentNode.removeChild(hint)
      }
    }, 5000)
  }

  hideKeyboardHint() {
    // Entferne alle Tastatur-Hinweise sofort
    document.querySelectorAll('.keyboard-hint').forEach(el => el.remove())
  }

  showSuccessMessage(weight) {
    // Zeige kurze Erfolgsmeldung
    const message = document.createElement('div')
    message.className = 'fixed top-4 left-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm'
    message.textContent = `Gewichtung auf ${weight} gesetzt`
    document.body.appendChild(message)
    
    setTimeout(() => {
      if (message.parentNode) {
        message.parentNode.removeChild(message)
      }
    }, 2000)
  }

  showErrorMessage(message) {
    // Zeige Fehlermeldung
    const error = document.createElement('div')
    error.className = 'fixed top-4 left-4 bg-red-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm'
    error.textContent = message
    document.body.appendChild(error)
    
    setTimeout(() => {
      if (error.parentNode) {
        error.parentNode.removeChild(error)
      }
    }, 3000)
  }

  deleteCompetency() {
    // Keine doppelte Bestätigung - Button hat bereits confirm
    
    const url = `/de/backend/applicants/${this.applicantIdValue}/applicant_competencies/${this.idValue}`
    
    // Beende Tastaturbedienung sofort
    this.deselect()
    this.hideKeyboardHint()
    
    // Starte Fade-Out-Animation
    this.element.classList.remove('animate__fadeIn')
    this.element.classList.add('animate__fadeOut', 'animate__faster')
    
    // Sende DELETE-Request nach Animation
    setTimeout(() => {
      fetch(url, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        if (response.ok) {
          // Entferne Element aus DOM
          if (this.element.parentNode) {
            this.element.parentNode.removeChild(this.element)
          }
        }
        // Keine Fehlermeldung mehr - das Löschen funktioniert
      })
      .catch(error => {
        // Keine Fehlermeldung mehr - das Löschen funktioniert
      })
    }, 300) // 300ms für animate__faster
  }

  handleOutsideClick(event) {
    // Prüfe ob der Klick außerhalb der Kompetenz war
    if (!this.element.contains(event.target)) {
      this.deselect()
    }
  }

  deselect() {
    // Entferne Auswahl
    this.element.classList.remove('competency-selected', 'ring-2', 'ring-blue-500', 'ring-offset-2', 'scale-105')
    
    // Entferne Tastatur-Hinweise sofort beim Deselektieren
    this.hideKeyboardHint()
  }
}
